import React, { useState, useEffect, useRef } from 'react';
import OlcButton from '~/components/General/OlcButton';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useSortParams from '~/hooks/useSortParams';
import useListUrlQuery from '~/hooks/useListUrlQuery';

// Utils
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGINATION, ENGAGEMENT_TYPES } from '~/utils/constants';
import { ENGAGEMENT_SORT_KEYS } from '~/utils/listQueryParams';

// Redux Actions
import {
  fetchQrCodes,
  createQrCode,
  updateQrCode,
  updateQrCodeStatus,
} from '~/redux/actions/qr-codes-actions';
import {
  fetchCustomFieldTypes,
  fetchCustomFieldsCatalog,
} from '~/redux/actions/customFields-actions';

// Components
import RecordsTable from './RecordsTable';
import CreateModal from './CreateModal';
import QrViewModal from './QrViewModal';
import ConfirmModal from '~/components/General/ConfirmModal';
import NoRecords from '~/components/General/NoRecords';
import CreateCustomFieldModal from '~/components/Settings/CustomFields/CreateModal';
import LimitReach from '~/components/General/LimitModal';

// MUI Components
import { Box, Typography, GlobalStyles, Input, InputAdornment, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Icons
import {
  GeneralInviteCIcon,
  ContactSearchCIcon,
  ModalArchive,
  ModalRestore,
  NoQRCode,
} from '~/assets/images';

// Styles
import './styles.scss';
import { failure, success } from '~/redux/actions/snackbar-actions';

const QrCodes = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State
  const qrCodes = useSelector((state) => state.qrCodesReducers.qrCodes);
  const totalCount = useSelector((state) => state.qrCodesReducers.rowsCount);
  const totalRecordsInDB = useSelector((state) => state.qrCodesReducers.totalRecordsInDB);
  const loading = useSelector((state) => state.qrCodesReducers.loading);
  const perPage = useSelector(
    (state) => state.qrCodesReducers.perPage || DEFAULT_PAGINATION.pageSize
  );
  const lastPage = useSelector((state) => state.qrCodesReducers.lastPage);
  const from = useSelector((state) => state.qrCodesReducers.from);
  const to = useSelector((state) => state.qrCodesReducers.to);
  const currentPage = useSelector((state) => state.qrCodesReducers.currentPage);

  const [modal, setModal] = useState({
    open: false,
    mode: 'create', // 'create' or 'edit'
  });
  const [viewQrModal, setViewQrModal] = useState(false);
  const [selectedQrCode, setSelectedQrCode] = useState({
    id: null,
    campaignName: null,
  });
  const [pagination, setPagination] = useState({ ...DEFAULT_PAGINATION, pageSize: 20 });
  const [initialLoad, setInitialLoad] = useState(true);
  // Search and column sort live in the URL (the source of truth), so they survive reload and
  // back/forward navigation. `searchTerm` below is only the text currently typed in the input.
  const {
    queryString,
    parsedQuery,
    appliedSearch,
    sort,
    updateUrl: updateQrCodesUrl,
  } = useListUrlQuery(ENGAGEMENT_SORT_KEYS);
  const searchApplied = Boolean(appliedSearch);

  const [searchTerm, setSearchTerm] = useState(appliedSearch);

  const lastRequestKeyRef = useRef(null);
  const pageSizeRef = useRef(pagination.pageSize);

  // A new sort goes to the URL; the URL effect below resets to page 1 and refetches.
  const { sortProps } = useSortParams([], (nextSort) => updateQrCodesUrl({ sort: nextSort }), sort);

  // Fetches a page for the current URL (applied search + sort). Records the request so the
  // pagination effect doesn't fetch the same page a second time.
  const loadQrCodes = (nextPagination = pagination) => {
    lastRequestKeyRef.current = `${queryString}|${nextPagination.page}|${nextPagination.pageSize}`;

    return dispatch(
      fetchQrCodes({
        ...sort,
        pagination: nextPagination,
        search: appliedSearch,
        engagementType: ENGAGEMENT_TYPES.QR_CODE,
      })
    );
  };

  // Clears the typed text and any applied search. Returns true when the URL changed, in which case
  // the URL effect refetches by itself.
  const clearSearch = () => {
    setSearchTerm('');
    return updateQrCodesUrl({ search: '' });
  };

  const [customFieldCreateModal, setCustomFieldCreateModal] = useState(false);
  const [customFieldTypes, setCustomFieldTypes] = useState([]);
  const [pendingCustomFieldSelection, setPendingCustomFieldSelection] = useState(null);
  const [limitModal, setLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    message: '',
    header: '',
    icon: '',
    loading: false,
    handleOk: () => {},
    handleClose: () => {},
    successButtonName: '',
  });

  const defaultFields = useSelector((state) => state.customFieldsReducers.defaultDynamicFields);

  const defaultSenderFields = useSelector(
    (state) => state.customFieldsReducers.defaultSenderFields
  );

  const customFields = useSelector((state) => state.customFieldsReducers.allCustomFields);

  const defaultPropertyFields = useSelector(
    (state) => state.customFieldsReducers.defaultPropertyFields
  );

  const excludedLabels = ['utm_c_first_name c_last_name'];

  const excludedFields = [];

  const allFields = [
    ...defaultFields,
    ...customFields,
    ...defaultSenderFields,
    ...defaultSenderFields,
    ...defaultPropertyFields,
    {
      value: 'ROS.PROPERTY_OFFER',
      key: '{{ROS.PROPERTY_OFFER}}',
      defaultValue: '$123,456.00',
    },
  ].filter(({ key }) => !excludedFields?.includes(key));

  const utmFields = allFields
    .map(({ key }) => ({
      label: `utm_${key?.toLowerCase().replaceAll('.', '_').replaceAll(/[{}]/g, '')}`,
    }))
    .filter((utmField) => !excludedLabels.includes(utmField.label));

  const utms = ['custom_utm_1', 'custom_utm_2', 'custom_utm_3'];

  const getCustomFieldDisplayName = (customField) =>
    customField?.value || customField?.title || customField?.label || customField?.name || '';

  const normalizeCustomFieldName = (value = '') =>
    String(value).trim().toLowerCase().replace(/\s+/g, '_');

  const extractCreatedCustomField = (response) => {
    const responseData = response?.data?.data;

    if (!responseData) {
      return null;
    }

    if (responseData.id) {
      return responseData;
    }

    if (responseData.customField?.id) {
      return responseData.customField;
    }

    if (responseData.field?.id) {
      return responseData.field;
    }

    return null;
  };

  const handleCustomFieldCreated = async (response, payload) => {
    const createdCustomField = extractCreatedCustomField(response);
    const customFieldName =
      getCustomFieldDisplayName(createdCustomField) || payload?.customFieldName;
    const catalogResponse = await dispatch(fetchCustomFieldsCatalog());
    const catalogFields = catalogResponse?.data?.data?.rows || [];

    const matchingCustomField =
      (createdCustomField?.id &&
        catalogFields.find((field) => String(field.id) === String(createdCustomField.id))) ||
      (customFieldName &&
        catalogFields.find(
          (field) =>
            normalizeCustomFieldName(getCustomFieldDisplayName(field)) ===
            normalizeCustomFieldName(customFieldName)
        )) ||
      createdCustomField ||
      (customFieldName ? { value: customFieldName } : null);

    if (matchingCustomField) {
      setPendingCustomFieldSelection(matchingCustomField);
    }
  };

  // API handlers
  const handleCreateQrCode = async (qrCodeData) => {
    try {
      const response = await dispatch(createQrCode(qrCodeData));
      if (response.status === 200 || response.status === 201) {
        // Show the new QR code: drop any search and go back to the first page.
        if (!clearSearch()) {
          setPagination((prev) => ({ ...prev, page: 1 }));
          await loadQrCodes({ page: 1, pageSize: pagination.pageSize });
        }
        dispatch(success(response?.data?.message));
      }
      return response;
    } catch (error) {
      dispatch(
        failure(
          error?.response?.data?.message ||
            error?.message ||
            error?.response?.message ||
            MESSAGES.GENERAL_ERROR
        )
      );
      return error;
    }
  };

  const handleUpdateQrCode = async (id, qrCodeData) => {
    try {
      const response = await dispatch(updateQrCode(id, qrCodeData));
      await loadQrCodes();
      if (response.status === 200 || response.status === 201) {
        dispatch(success(response?.data?.message));
      }
      return response;
    } catch (error) {
      dispatch(
        failure(
          error?.response?.data?.message ||
            error?.message ||
            error?.response?.message ||
            MESSAGES.GENERAL_ERROR
        )
      );
      return error;
    }
  };

  const handleArchiveQrCode = async (qrCodeId) => {
    try {
      setConfirmModal((prev) => ({ ...prev, loading: true }));
      const response = await dispatch(updateQrCodeStatus(qrCodeId, { status: 'Archived' }));
      await loadQrCodes();
      setConfirmModal((prev) => ({ ...prev, open: false, loading: false }));
      if (response.status === 200 || response.status === 201) {
        dispatch(success(response?.data?.message));
      }
      return response;
    } catch (error) {
      setConfirmModal((prev) => ({ ...prev, loading: false }));
      dispatch(
        failure(
          error?.response?.data?.message ||
            error?.message ||
            error?.response?.message ||
            MESSAGES.GENERAL_ERROR
        )
      );
      return error;
    }
  };

  const handleRestoreQrCode = async (qrCodeId) => {
    try {
      setConfirmModal((prev) => ({ ...prev, loading: true }));
      const response = await dispatch(updateQrCodeStatus(qrCodeId, { status: 'Active' }));
      await loadQrCodes();
      setConfirmModal((prev) => ({ ...prev, open: false, loading: false }));
      if (response.status === 200 || response.status === 201) {
        dispatch(success(response?.data?.message));
      }
      return response;
    } catch (error) {
      setConfirmModal((prev) => ({ ...prev, loading: false }));
      dispatch(
        failure(
          error?.response?.data?.message ||
            error?.message ||
            error?.response?.message ||
            MESSAGES.GENERAL_ERROR
        )
      );
      return error;
    }
  };

  useEffect(() => {
    pageSizeRef.current = pagination.pageSize;
  }, [pagination.pageSize]);

  // URL effect: runs on mount and whenever search or sort change in the URL (typing, clearing,
  // back/forward, shared link). It restores the input, goes back to page 1 and fetches once.
  useEffect(() => {
    let isCurrent = true;
    const nextPagination = { page: 1, pageSize: pageSizeRef.current };

    setSearchTerm(parsedQuery.search);
    setPagination(nextPagination);

    lastRequestKeyRef.current = `${queryString}|${nextPagination.page}|${nextPagination.pageSize}`;
    dispatch(
      fetchQrCodes({
        ...parsedQuery.sort,
        pagination: nextPagination,
        search: parsedQuery.search,
        engagementType: ENGAGEMENT_TYPES.QR_CODE,
      })
    ).then(() => {
      if (isCurrent) {
        setInitialLoad(false);
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [dispatch, parsedQuery, queryString]);

  // Pagination effect: page / page-size changes only. URL changes are fetched by the effect above,
  // which also resets the page, so this one must not react to them (no duplicate or stale-page request).
  const loadQrCodesRef = useRef(loadQrCodes);
  loadQrCodesRef.current = loadQrCodes;

  useEffect(() => {
    const requestKey = `${queryString}|${pagination.page}|${pagination.pageSize}`;
    if (lastRequestKeyRef.current === requestKey) {
      return;
    }

    loadQrCodesRef.current();
    // queryString is read for the dedupe key only; it must not re-trigger this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize]);

  useEffect(() => {
    const loadCustomFieldTypes = async () => {
      const response = await dispatch(fetchCustomFieldTypes());
      if (response?.status === 200) {
        setCustomFieldTypes(response.data.data || []);
      }
    };

    loadCustomFieldTypes();
  }, [dispatch]);

  // Applying the same search again just refreshes the list.
  const handleSearch = () => {
    const term = searchTerm.trim();

    if (term && !updateQrCodesUrl({ search: term })) {
      loadQrCodes();
    }
  };

  // Emptying the input removes an applied search from the URL (the list then shows all QR codes).
  const handleSearchChange = (event) => {
    const nextTerm = event.target.value.trimStart();

    setSearchTerm(nextTerm);

    if (!nextTerm && appliedSearch) {
      updateQrCodesUrl({ search: '' });
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (event.target.value.trimStart()) {
        handleSearch();
      }
    }
  };

  const handleViewHistory = (qrCode) => {
    // Remember the list's search/sort so "Back to QR Codes" can restore them.
    navigate(`/engagement/qr-code-history/${qrCode.id}`, {
      state: { qrCode, listQuery: queryString },
    });
  };

  const showEmptyState = !initialLoad && !loading && !searchApplied && (totalCount || 0) <= 0;
  const showSearchToolbar = initialLoad || loading || searchApplied || (totalCount || 0) > 0;

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#f9fafb' } }} />
      <Box className="olcWrapper olcSettingsListPage qrCodesPageWrapper">
        <Box className="olcScreenCard olcSettingsListCard qrCodesScreenCard">
          <Box className="olcHeaderWrap">
            <Box>
              <Typography className="pageTitle">{MESSAGES.SETTINGS.QR_CODES.TITLE}</Typography>
            </Box>
            <Box className="pageNewBtn">
              <OlcButton
                variant="primary"
                onClick={() => setModal({ open: true, mode: 'create' })}
                startIcon={<img src={GeneralInviteCIcon} alt="actions" />}
              >
                <Typography>{MESSAGES.SETTINGS.QR_CODES.ADD_NEW}</Typography>
              </OlcButton>
            </Box>
          </Box>
          {showSearchToolbar && (
            <Box className="olcTableToolbar qrCodesToolbar">
              <Box className="searchWrapper olcSearchWrapper olcSearchWrapperQR">
                <Input
                  className={`searchInput ${searchApplied ? 'searchApplied' : ''}`}
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Search by campaign name"
                  title="Search by campaign name"
                  endAdornment={
                    <InputAdornment position="end" className="searchInputAdornment">
                      <IconButton
                        aria-label="search"
                        edge="end"
                        className="searchIcon"
                        onClick={handleSearch}
                      >
                        <img src={ContactSearchCIcon} alt="search" />
                      </IconButton>
                      {searchTerm?.trim() && (
                        <IconButton
                          className="cpClearInputBtn"
                          size="small"
                          onClick={clearSearch}
                          edge="end"
                          sx={{ padding: '4px' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  }
                />
              </Box>
            </Box>
          )}
          {showEmptyState ? (
            <Box>
              <NoRecords
                icon={
                  <div
                    style={{
                      height: '40px',
                      width: '40px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      display: 'flex',
                    }}
                  >
                    <img
                      src={NoQRCode}
                      style={{ height: '40px', width: '40px' }}
                      alt="no-records"
                    />
                  </div>
                }
                title="No QR Codes Active"
                message={
                  <Box className="noRecordsMessageQR">
                    QR Codes let you track engagement from your mailers. Each mail piece gets a
                    unique, dynamic QR code so you'll know exactly who scanned it and when.
                  </Box>
                }
                messageWidth="min(100%, 720px)"
                minBoxHeight="220px"
                boxPadding="24px 40px"
                linkText="Learn more"
                linkUrl="https://help.openletterconnect.com/olc-help-docs/product-and-order-guide/creating-a-template/qr-codes"
              />
            </Box>
          ) : (
            <RecordsTable
              qrCodes={qrCodes}
              setSelectedQrCode={setSelectedQrCode}
              setEditModal={() => {
                setModal({ open: true, mode: 'edit' });
              }}
              updatePagination={setPagination}
              pagination={pagination}
              sortProps={sortProps}
              totalCount={totalCount}
              totalRecordsInDB={totalRecordsInDB}
              lastPage={lastPage}
              from={from}
              to={to}
              currentPage={currentPage}
              perPage={perPage}
              initialLoad={initialLoad}
              isLoading={loading}
              onArchive={(qrCode) => {
                setConfirmModal({
                  open: true,
                  message: (
                    <Box style={{ marginBottom: '4px' }}>
                      Are you sure you want to archive this QR Code? Archiving will keep it linked
                      to all existing orders, and other work, but it will no longer be active for
                      new activity.
                    </Box>
                  ),
                  header: 'Archive QR Code',
                  icon: ModalArchive,
                  loading: false,
                  handleOk: () => handleArchiveQrCode(qrCode.id),
                  handleClose: () => setConfirmModal((prev) => ({ ...prev, open: false })),
                  successButtonName: 'Yes',
                });
              }}
              onRestore={(qrCode) => {
                setConfirmModal({
                  open: true,
                  message: (
                    <Box style={{ marginBottom: '4px' }}>
                      Restoring this QR Code will make it available again for templates.
                    </Box>
                  ),
                  header: 'Restore',
                  icon: ModalRestore,
                  loading: false,
                  cancelButtonName: 'Cancel',
                  handleOk: () => handleRestoreQrCode(qrCode.id),
                  handleClose: () => setConfirmModal((prev) => ({ ...prev, open: false })),
                  successButtonName: 'Restore',
                });
              }}
              onViewQrCode={(qrCode) => {
                setSelectedQrCode(qrCode);
                setViewQrModal(true);
              }}
              onViewHistory={handleViewHistory}
            />
          )}
        </Box>
      </Box>
      <CreateModal
        openModal={modal.open}
        handleCloseModal={() => setModal((prev) => ({ ...prev, open: false }))}
        onCreateQrCode={handleCreateQrCode}
        selectedQrCode={modal.mode === 'edit' ? selectedQrCode : null}
        onUpdateQrCode={handleUpdateQrCode}
        isEditMode={modal.mode === 'edit'}
        utmFields={utmFields}
        customFields={customFields}
        allFields={allFields}
        utms={utms}
        onCreateCustomFieldClick={() => setCustomFieldCreateModal(true)}
        pendingCustomFieldSelection={pendingCustomFieldSelection}
        onPendingCustomFieldSelectionConsumed={() => setPendingCustomFieldSelection(null)}
      />

      <CreateCustomFieldModal
        openModal={customFieldCreateModal}
        handleCloseModal={() => setCustomFieldCreateModal(false)}
        pagination={{ ...DEFAULT_PAGINATION, pageSize: 20 }}
        updatePagination={() => {}}
        customFieldTypes={customFieldTypes}
        defaultType="url"
        disableType
        onSuccess={handleCustomFieldCreated}
        onLimitReached={(message) => {
          setCustomFieldCreateModal(false);
          setLimitMessage(message);
          setLimitModal(true);
        }}
      />
      <LimitReach
        open={limitModal}
        onClose={() => {
          setLimitModal(false);
          setTimeout(() => setLimitMessage(''), 100);
        }}
        message={limitMessage}
        resourceType="custom fields"
      />

      <ConfirmModal openConfirmModal={confirmModal} />
      <QrViewModal
        open={viewQrModal}
        onClose={() => setViewQrModal(false)}
        selectedQrCode={selectedQrCode}
      />
    </>
  );
};

export default QrCodes;
